/**
 * Named items: the authored objects at the top of the rarity ladder.
 * Unforgotten (`legendary`) items come from a Crucible's heart and its
 * guardian; Empyreal (`mythic`) items are only granted by authored end-game
 * sources. Each has four fixed affixes and at least one unique behavior, and
 * is never upgraded or rerolled. See docs/gameplay/item-rarities.md.
 *
 * A copy keeps only its definition id (`ItemInstance.namedItemId`), so a
 * rebalanced definition updates every copy. The definitions are game content
 * (game-content/data/named-items/), authored in the Named Item Designer.
 */

import type { ItemAffixRoll, ItemRarity } from './types/game';
import type { EssenceType } from './types/essence';
import type { NamedItemBehavior } from './types/behaviors';
import type { ItemEffectKind } from './item-effect-kinds';
import { clampItemLevel, findItemAffix, ITEM_LEVEL_MAX, resolveAffixValue } from './item-affixes';

/** The rarities that are named items. */
export type NamedItemRarity = Extract<ItemRarity, 'legendary' | 'mythic'>;

/**
 * How a named item's effect is drawn on its model, in place of its
 * rarity's: each part left out keeps the rarity's (see the client's
 * `resolveItemEffectLook`).
 */
export interface NamedItemLook {
    /** The effect's color, as #rrggbb. */
    readonly colorHex?: string;
    /** Which effects it shows (`ITEM_EFFECT_KINDS`), in place of its rarity's; an empty list shows none. */
    readonly effects?: readonly ItemEffectKind[];
    /** 0 to 1: how bright and lively the layers are. */
    readonly strength?: number;
}

/** How many fixed affixes every named item carries. */
export const NAMED_ITEM_AFFIX_COUNT = 4;

/** An Empyreal item's affixes are this much past the top of their max-level range. */
export const EMPYREAL_AFFIX_SCALE = 1.5;

export interface NamedItemDefinition {
    /** Plain slug, like affix ids. */
    readonly id: string;
    /** What players read; replaces the base item's name. */
    readonly name: string;
    readonly rarity: NamedItemRarity;
    /** The catalog item (`game-content/data/items/<id>.json`, by its id) it is built on: model, base stats, requirements. */
    readonly baseItemId: string;
    /** Exactly `NAMED_ITEM_AFFIX_COUNT` affix ids the base can take, from different groups. */
    readonly affixIds: readonly string[];
    readonly behaviors: readonly NamedItemBehavior[];
    /** What the behaviors do, as the item card reads. */
    readonly description: string;
    /** Its own flavor line, used instead of a random `ITEM_FLAVOR` pick. */
    readonly flavor: string;
    /** Unforgotten only: the shallowest item level it can drop at. */
    readonly minItemLevel?: number;
    /** The essence it leans to: a Crucible dominant in it drops it more often. */
    readonly essence: EssenceType;
    /** Its own visual effect; the rarity's when left out. */
    readonly look?: NamedItemLook;
}

/**
 * Every named item this process knows, from game-content/data/named-items/
 * (one definition per file). Empty until it is loaded: the server loads the
 * files (`NamedItemCatalog` in @sorrem/server), a crucible host takes the run's
 * copy from its state file, and the client fetches them. Functions that
 * take a `namedCatalog` default to this, so a test can pass its own.
 */
let namedItemCatalog: readonly NamedItemDefinition[] = [];

export function getNamedItemCatalog(): readonly NamedItemDefinition[] {
    return namedItemCatalog;
}

/** Replaces the catalog, as when its files are (re)loaded. */
export function setNamedItemCatalog(catalog: readonly NamedItemDefinition[]): void {
    namedItemCatalog = catalog;
}

export function isNamedItemRarity(rarity: ItemRarity): rarity is NamedItemRarity {
    return rarity === 'legendary' || rarity === 'mythic';
}

export function findNamedItem(
    namedItemId: string | undefined,
    catalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
): NamedItemDefinition | undefined {
    return namedItemId === undefined ? undefined : catalog.find((definition) => definition.id === namedItemId);
}

/** The item level a named copy found at `itemLevel` has: Empyreal items are always at the cap. */
export function resolveNamedItemLevel(definition: Pick<NamedItemDefinition, 'rarity'>, itemLevel: number): number {
    return definition.rarity === 'mythic' ? ITEM_LEVEL_MAX : clampItemLevel(itemLevel);
}

/**
 * A named item's fixed affix values at an item level: the top of each range
 * for Unforgotten, and past the max-level top for Empyreal. Unknown affix ids
 * are skipped.
 */
export function resolveNamedItemAffixes(
    definition: Pick<NamedItemDefinition, 'rarity' | 'affixIds'>,
    itemLevel: number,
): ItemAffixRoll[] {
    const level = resolveNamedItemLevel(definition, itemLevel);
    return definition.affixIds.flatMap((affixId) => {
        const affix = findItemAffix(affixId);
        if (!affix) {
            return [];
        }
        const top = resolveAffixValue(affix, level, 1);
        if (definition.rarity !== 'mythic') {
            return [{ affixId, value: top }];
        }
        const scaled = top * EMPYREAL_AFFIX_SCALE;
        const value = affix.isInteger ? Math.round(scaled) : Math.round(scaled * 1000) / 1000;
        return [{ affixId, value }];
    });
}
